import React from 'react';
import { ArrowUp, ArrowDown, Smartphone, UserCircle, ListTodo, CheckCircle } from 'lucide-react';
import { Card } from '@/shared/ui';
import { useDashboardSummary } from '@/entities/dashboard';
import { formatNumber } from '@/shared/lib/utils';

interface StatCardProps {
  title: string;
  value: string;
  change: number;
  icon: string;
  iconColor: string;
}

const getStatIcon = (iconName: string) => {
  const iconMap: Record<string, React.ReactNode> = {
    'fa-mobile': <Smartphone className="w-5 h-5" />,
    'fa-user-circle': <UserCircle className="w-5 h-5" />,
    'fa-tasks': <ListTodo className="w-5 h-5" />,
    'fa-check-circle': <CheckCircle className="w-5 h-5" />,
  };
  return iconMap[iconName] || <div className="w-5 h-5" />;
};

const StatCard: React.FC<StatCardProps> = ({ title, value, change, icon, iconColor }) => {
  const isPositive = change >= 0;
  
  return (
    <Card hover className="p-6">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-gray-500 text-sm text-left">{title}</p>
          <h3 className="text-2xl font-semibold mt-1 text-left">{value}</h3>
          <p className={`text-xs mt-2 flex items-center ${
            isPositive ? 'text-success' : 'text-danger'
          }`}>
            {isPositive ? <ArrowUp className="w-3 h-3 mr-1" /> : <ArrowDown className="w-3 h-3 mr-1" />}
            어제보다 {isPositive ? '+' : ''}{change}{title.includes('율') ? '%' : ''}
          </p>
        </div>
        <div className={`w-10 h-10 rounded-full ${iconColor} flex items-center justify-center`}>
          {getStatIcon(icon)}
        </div>
      </div>
    </Card>
  );
};

export const StatsCards: React.FC = () => {
  const { data: summaryResponse, isLoading } = useDashboardSummary();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="p-6 animate-pulse">
            <div className="flex justify-between items-start">
              <div>
                <div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-20 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-24"></div>
              </div>
              <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  const summary = summaryResponse?.data;

  if (!summary) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <StatCard
        title="단말기 대수"
        value={formatNumber(summary.connected_devices.current)}
        change={summary.connected_devices.change_from_yesterday}
        icon="fa-mobile"
        iconColor="bg-primary/10 text-primary"
      />

      <StatCard
        title="SNS 계정"
        value={formatNumber(summary.sns_accounts.current)}
        change={summary.sns_accounts.change_from_yesterday}
        icon="fa-user-circle"
        iconColor="bg-secondary/10 text-secondary"
      />

      <StatCard
        title="작업"
        value={formatNumber(summary.tasks.current)}
        change={summary.tasks.change_from_yesterday}
        icon="fa-tasks"
        iconColor="bg-warning/10 text-warning"
      />

      <StatCard
        title="진행률"
        value={`${summary.task_success_rate.current_rate}%`}
        change={summary.task_success_rate.change_from_yesterday}
        icon="fa-check-circle"
        iconColor="bg-success/10 text-success"
      />
    </div>
  );
};